import * as Sentry from '@sentry/react'

import { PUBLIC_SENTRY_DSN } from '@/lib/env'

let initialized = false

// Anything that looks like an email address or a long digit run (card-ish or
// account-ish) in free-text we send to Sentry.
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g
const LONG_DIGIT_RUN_PATTERN = /\b\d{9,}\b/g

function redactString(value: string) {
  return value.replace(EMAIL_PATTERN, '[email]').replace(LONG_DIGIT_RUN_PATTERN, '[redacted]')
}

// Recursive scrub with a depth bound — error `extra` payloads are
// caller-supplied and can be deeply nested or cyclic.
function redactValue(value: unknown, depth = 0): unknown {
  if (depth > 4) return '[truncated]'
  if (typeof value === 'string') return redactString(value)
  if (Array.isArray(value)) return value.slice(0, 50).map((entry) => redactValue(entry, depth + 1))
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, redactValue(entry, depth + 1)]),
    )
  }
  return value
}

/**
 * Last line of defence before an event leaves the browser.
 *
 * captureClientError forwards arbitrary caller-supplied details as `extra`, and
 * the blank-screen watchdog reports the URL query string (which carries donation
 * prefill params). Neither is guaranteed free of donor data, so scrub both here
 * rather than trusting every call site to be careful.
 */
function scrubEventPii(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request?.query_string) {
    event.request.query_string = '[redacted]'
  }
  if (event.request?.url) {
    event.request.url = event.request.url.split('?')[0]
  }
  if (event.extra) {
    event.extra = redactValue(event.extra) as Record<string, unknown>
  }
  if (event.message) {
    event.message = redactString(event.message)
  }
  return event
}

// Initializes Sentry on the client. Safe to call during SSG prerender (no-op
// without a window) and safe to call before a DSN is configured (no-op without
// PUBLIC_SENTRY_DSN), so this can ship before the Sentry project exists and
// "light up" the moment VITE_SENTRY_DSN is set in the environment.
export function initSentry() {
  if (initialized) return
  if (typeof window === 'undefined') return
  if (!PUBLIC_SENTRY_DSN) return
  // Preview deployments share the Preview-scoped DSN but are not the product:
  // they are branch builds the team opens by hand, and a preview without a
  // backend would page everyone about it. Keep Sentry for the live site (and
  // local dev when a DSN is set); a preview's problems are visible to the
  // person testing it.
  if (__DEPLOY_ENV__ === 'preview') return

  Sentry.init({
    dsn: PUBLIC_SENTRY_DSN,
    // production | development. Vite's MODE is 'production' for every build,
    // which is how preview builds used to show up as production incidents.
    environment: __DEPLOY_ENV__,
    // Ties every event to the deploy that produced it (undefined locally, so
    // dev sessions do not create bogus releases).
    release: __COMMIT_SHA__ === 'dev' ? undefined : __COMMIT_SHA__,
    // Errors thrown by scripts that in-app browsers inject into the page, not
    // by KindLumen: the Facebook app's iOS WebView calls
    // window.webkit.messageHandlers[...] handlers that do not exist, and its
    // Android WebView's performance logger (iabjs://...) calls a native bridge
    // that is already gone once the page unloads. Both arrive with Facebook ad
    // traffic and nothing in our code can fix them.
    ignoreErrors: [
      /window\.webkit\.messageHandlers/,
      /Error invoking postMessage: Java/,
      // Snapchat on iOS injects this missing native bridge call (the spelling
      // is intentional). It is attributed to the page HTML at line 1, so a URL
      // filter would also hide our errors. Match only this ReferenceError.
      /^(?:ReferenceError: )?Can't find variable: SCDynimacBridge$/,
    ],
    denyUrls: [/^iabjs:\/\//],
    // Capture unhandled errors + promise rejections automatically (default
    // GlobalHandlers integration). Add navigation/console/fetch breadcrumbs and
    // a session replay whenever an error occurs — enough to diagnose a blank
    // screen, since layout and interaction are preserved even with text masked.
    //
    // Text and media are MASKED. These replays run on the donor checkout page
    // and the org dashboard, so unmasked they would ship donor emails, gift
    // amounts, and whole donor lists to a third party. `sendDefaultPii: false`
    // does NOT cover this — it governs Sentry's own IP/user attachment, not
    // replay DOM capture. Diagnosing a rendering bug does not require reading
    // what the donor typed.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: scrubEventPii,
  })

  initialized = true
}

export function isSentryInitialized() {
  return initialized
}

// Bridge for the existing logClientError() call sites. Picks the first Error in
// the details as the exception (so stack traces are preserved) and attaches the
// rest as context. No-op until Sentry is initialized.
export function captureClientError(message: string, ...details: unknown[]) {
  if (!initialized) return

  const error = details.find((detail): detail is Error => detail instanceof Error)

  if (error) {
    Sentry.captureException(error, { extra: { message, details: details.filter((d) => d !== error) } })
  } else {
    Sentry.captureMessage(message, { level: 'error', extra: { details } })
  }
}

// Catches the *silent* blank screen — where React never mounts so no error is
// thrown. If #root is still empty several seconds after boot, report it (with a
// session replay) so we can see what happened. Only arms when Sentry is on.
export function installBlankScreenWatchdog() {
  if (typeof window === 'undefined') return
  if (!initialized) return

  const check = () => {
    try {
      // Automation and headless browsers (navigator.webdriver) block scripts or
      // never mount the app on purpose; that is not a blank screen a person saw.
      if (navigator.webdriver) return
      const root = document.getElementById('root')
      const rootIsEmpty = !root || root.childElementCount === 0
      if (!rootIsEmpty) return

      // An exception rather than a message: Sentry only flushes the on-error
      // session replay for exception events, and the replay is what makes one
      // of these diagnosable (the message events collected so far had none).
      const error = new Error('Blank screen detected — #root still empty after boot')
      error.name = 'BlankScreenError'
      Sentry.captureException(error, {
        level: 'error',
        extra: {
          path: window.location.pathname,
          search: window.location.search,
          dataSpaShell: document.documentElement.getAttribute('data-spa-shell'),
          referrer: document.referrer,
          readyState: document.readyState,
          userAgent: navigator.userAgent,
        },
      })
    } catch {
      // Never let the watchdog itself throw.
    }
  }

  window.setTimeout(() => {
    if (document.readyState === 'complete') {
      check()
      return
    }
    // Still downloading at the 8s mark (slow network, or a stalled resource):
    // wait for load and give the app a few more seconds. Reporting now would
    // flag a page that is merely slow, which is what the readyState of the
    // real-browser reports collected so far showed.
    window.addEventListener('load', () => window.setTimeout(check, 4000), { once: true })
  }, 8000)
}
